import {Dropdown, Text, TextField} from '@/components/atoms'
import {usePromotion} from '@/context/Promotion/PromotionContext'
import {Condition, CurrencyType, PromotionCondition} from '@/types/promotion'
import {Constants} from '@/utils/constants/commonConstants'
import {ANY_BUY_OPTION, ANY_ID, PromotionContionsTabList} from '@/utils/constants/promotionConstants'
import {DropdownItem} from 'pepsico-ds'
import {FC, useEffect, useMemo, useRef, useState} from 'react'
import {useSelector} from 'react-redux'
import PromotionSelectionModal from '../PromotionSelectionModal'
import {RewardItem} from './Rewards/RewardItem'
import {useRewardValidations} from './Rewards/RewardValidations'

type BuySingleProductConditionsProps = {
  promotionCondition: PromotionCondition
  index: number
  selectedPromotionCondition?: string
}

export const BuySingleProductConditions: FC<BuySingleProductConditionsProps> = ({
  promotionCondition,
  index,
  selectedPromotionCondition = 'UN',
}) => {
  const prevPromotionCondition = useRef(selectedPromotionCondition)
  const {updatePromotion, promotionConfig, isLoadingConfig} = usePromotion()
  const {hasBuyQuantityLayerError} = useRewardValidations(promotionCondition)
  let buyTypeOptions = promotionConfig?.conditions?.itemTypeOptions ?? []
  const isAmountTab = selectedPromotionCondition === 'EA'
  const textAreavalue = isAmountTab
    ? (promotionCondition?.conditionGroup?.conditions?.[0]?.amount ??
      promotionCondition?.conditionGroup?.conditions?.[0]?.quantity)
    : promotionCondition?.conditionGroup?.conditions?.[0]?.quantity
  const [modalProps, setModalProps] = useState<{type: string | ''; show: boolean}>({show: false, type: ''})

  const {countryCode} = useSelector((state: any) => state?.countryCode)
  const currencyCode = CurrencyType[countryCode ?? 'MX'] ?? '$'
  const handleBuyCount = (values: DropdownItem[]) => {
    const value = values?.[0]?.id
    if (value) {
      // If ANY is selected in the quantity dropdown, set quantity to 1
      if (isNaN(value) && value.toLowerCase() === ANY_ID.toLowerCase()) {
        updateCurrentCondition({quantity: 1})
      } else {
        updateCurrentCondition({quantity: Number(value)})
      }
    } else {
      updateCurrentCondition({quantity: null})
    }
  }

  const updateCurrentCondition = (update: Partial<Condition>) => {
    updatePromotion(prev => {
      const updatedConditions = prev.promotionConditions.map(condition => {
        if (condition.sequence !== promotionCondition.sequence) return condition

        const updatedConditionGroup = {
          ...condition.conditionGroup,
          conditions: (condition.conditionGroup?.conditions || []).map((item, index) => {
            return index === 0 ? {...item, ...update} : item
          }),
        }

        return {
          ...condition,
          conditionGroup: updatedConditionGroup,
        }
      })

      return {
        ...prev,
        promotionConditions: updatedConditions,
      }
    })
  }

  const clearValues = () => {
    updateCurrentCondition({items: [], itemType: undefined})
  }

  const handleCloseModal = () => {
    setModalProps({type: '', show: false})
  }

  const handleValues = selectedItemsAndType => {
    const {values, itemType} = selectedItemsAndType || {}

    if (!values || !Array.isArray(values)) {
      clearValues()
      return
    }
    const items = values.map(item => ({
      id: item?.id,
      name: item?.displayText,
      uom: selectedPromotionCondition,
    }))

    updatePromotion((promotion: any) => ({
      ...promotion,
      promotionConditions: promotion?.promotionConditions?.map(condition => ({
        ...condition,
        conditionGroup: {
          ...condition.conditionGroup,
          conditions: condition?.conditionGroup?.conditions?.map(cond => ({
            ...cond,
            itemType: itemType,
            items: items,
          })),
        },
      })),
    }))
  }

  const handleSubmitModal = selectedItems => {
    handleValues(selectedItems)
    handleCloseModal()
  }

  const buyCount = promotionCondition?.conditionGroup?.conditions?.[0]?.quantity
  let buyQtyOptions = useMemo(
    () => promotionConfig?.buyQty?.map(value => ({displayText: value, id: Number(value)})),
    [promotionConfig?.buyQty]
  )

  const currentCondition = promotionCondition?.conditionGroup?.conditions?.[0]

  const items = currentCondition?.items ?? []

  const selectedValuesText = items.map(item => item.name).join(', ')
  const quantityValue = buyQtyOptions?.filter(item => item?.id === buyCount) ?? []
  const productsValue = items.map(item => ({id: item.id, displayText: item.name})) ?? []

  const isAnySelected = currentCondition?.items?.find(item => item.id === ANY_ID)
  const handleSelectedBuyItem = (values: DropdownItem[]) => {
    if (!values || !values.length) {
      clearValues()
      return
    }

    const isModalValue = values.find(item => buyTypeOptions?.find(({id}) => id === item?.id))

    let updatedItemType = currentCondition?.itemType
    let updatedValues = values

    if (isModalValue) {
      if (isModalValue?.id === ANY_ID) {
        updatedItemType = ANY_ID
        updatedValues = [{...ANY_BUY_OPTION, uom: PromotionContionsTabList[selectedPromotionCondition]?.uom} as any]
        handleValues({values: updatedValues, itemType: updatedItemType})
      } else {
        setModalProps({show: true, type: isModalValue?.id})
        return // stop execution if modal opens
      }
    } else {
      // For regular items
      handleValues({values, itemType: updatedItemType})
    }
  }
  const buyQtyOptionsData = new Set(quantityValue?.map(item => item?.id))

  if (buyQtyOptionsData?.has('any')) {
    buyTypeOptions = buyTypeOptions?.filter(item => item?.id !== ANY_ID)
  }
  if (isAnySelected) {
    buyQtyOptions = buyQtyOptions?.filter(item => item?.id !== ANY_ID)
  }

  const isFirstCondition = promotionCondition?.sequence === 1
  const isError = hasBuyQuantityLayerError()
  const handleBuyAmount = (value: string) => {
    updateCurrentCondition({
      amount: value ? Number(value) : null,
    })
  }
  useEffect(() => {
    // Only run if selectedPromotionCondition actually changed
    if (prevPromotionCondition.current === selectedPromotionCondition) return

    // 🔹 Update promotion with cleared quantity and updated UOM
    updatePromotion((promotion: any) => ({
      ...promotion,
      promotionConditions: promotion?.promotionConditions?.map(condition => ({
        ...condition,
        conditionGroup: {
          ...condition.conditionGroup,
          conditions: condition?.conditionGroup?.conditions?.map(cond => ({
            ...cond,
            items: cond?.items?.map(item => ({
              ...item,
              uom: selectedPromotionCondition, // update uom
            })),
          })),
        },
      })),
    }))
    prevPromotionCondition.current = selectedPromotionCondition
  }, [selectedPromotionCondition])
  const units = PromotionContionsTabList[0].uom
  const cases = PromotionContionsTabList[1].uom
  return (
    <div className="flex flex-wrap  items-baseline gap-4">
      <Text>Buy</Text>

      {selectedPromotionCondition === units || selectedPromotionCondition === cases ? (
        <Dropdown
          isJsonWrap={false}
          childList={buyQtyOptions}
          placeholder={ANY_ID}
          updateValue={handleBuyCount}
          isClearable={false}
          value={quantityValue}
          selection="single"
          // @ts-expect-error types
          autoComplete="off"
          style={{width: 84}}
          loading={isLoadingConfig}
          required={isFirstCondition}
          isError={isError}
          errorMessage="Value Invalid"
        />
      ) : (
        <>
          <Text>{currencyCode}</Text>
          <TextField
            updateValue={handleBuyAmount}
            value={textAreavalue?.toString() || ''}
            error={isError ? 'Value Invalid' : ''}
            uiSchema={{
              helperText: '',
              placeholder: '0.0',
            }}
            // @ts-ignore
            style={{maxWidth: '85px'}}
            type="number"
          />
        </>
      )}

      <div className={index > 0 ? 'pointer-events-none' : ''}>
        <Dropdown
          isJsonWrap={false}
          childList={buyTypeOptions}
          placeholder={selectedValuesText || Constants.AddProducts}
          value={[...productsValue]}
          updateValue={handleSelectedBuyItem}
          isClearable={true}
          // @ts-expect-error types
          autoComplete="off"
          loading={isLoadingConfig}
          required={isFirstCondition}
          selection="multiple"
          disabled={index > 0}
        />
      </div>
      <Text>and get</Text>
      <RewardItem index={index} promotionCondition={promotionCondition} />

      <PromotionSelectionModal
        key={JSON.stringify(modalProps.type)}
        show={modalProps.show}
        type={modalProps.type}
        onClose={handleCloseModal}
        onSubmit={handleSubmitModal}
        conditionValues={{
          itemType: currentCondition?.itemType || '',
          values: productsValue,
          productCombination: currentCondition?.productCombination,
        }}
      />
    </div>
  )
}
